/**
 * Cart Management - Shopify Dawn Theme Pattern
 * Single source of truth for all cart functionality
 */

class CartManager {
  constructor() {
    this.cart = null;
    this.updating = false;
    this.init();
  }

  async init() {
    await this.fetchCart();
    this.setupEventListeners();
  }

  // Fetch current cart state
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
      return this.cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  // Check if we're on the cart page
  isCartPage() {
    return document.getElementById('main-cart-items') !== null;
  }

  // Get sections to render for cart page (Dawn pattern)
  getSectionsToRender() {
    const sections = [];
    const mainCart = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (mainCart && mainCart.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCart.dataset.id,
        selector: '.js-contents'
      });
    }
    if (cartFooter && cartFooter.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: cartFooter.dataset.id,
        selector: '.js-contents'
      });
    }
    return sections;
  }

  // Add item to cart
  async addItem(formData) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add item to cart');
      }

      const item = await response.json();
      await this.fetchCart();
      this.updateCartUI();
      this.showCartDrawer();

      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item, cart: this.cart }
      }));

      return item;
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message);
      throw error;
    }
  }

  // Update cart item - handles both cart page and drawer
  async updateItem(line, quantity) {
    if (this.updating) return;
    this.updating = true;

    document.body.classList.add('cart-updating');

    try {
      const sections = this.getSectionsToRender();
      const sectionNames = sections.map(s => s.section);

      // Use bundled section rendering (Dawn pattern)
      const body = {
        line: line,
        quantity: quantity
      };

      // Only add sections if on cart page
      if (sections.length > 0) {
        body.sections = sectionNames;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const state = await response.json();
      this.cart = state;

      // Handle empty cart
      if (state.item_count === 0 && this.isCartPage()) {
        window.location.reload();
        return state;
      }

      // Update cart page sections if available
      if (state.sections && sections.length > 0) {
        sections.forEach(section => {
          const html = state.sections[section.section];
          if (!html) return;

          const container = document.getElementById(section.id);
          if (!container) return;

          const temp = document.createElement('div');
          temp.innerHTML = html;

          const newContent = temp.querySelector(section.selector);
          const oldContent = container.querySelector(section.selector);

          if (newContent && oldContent) {
            oldContent.innerHTML = newContent.innerHTML;
          }
        });
      }

      // Update drawer and cart count
      this.updateCartUI();

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: this.cart }
      }));

      return this.cart;
    } catch (error) {
      console.error('Error updating cart:', error);
      if (this.isCartPage()) {
        window.location.reload();
      }
      throw error;
    } finally {
      this.updating = false;
      document.body.classList.remove('cart-updating');
    }
  }

  // Remove item from cart
  async removeItem(line) {
    return this.updateItem(line, 0);
  }

  // Clear entire cart
  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to clear cart');

      await this.fetchCart();
      this.updateCartUI();

      document.dispatchEvent(new CustomEvent('cart:cleared'));

      return this.cart;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  // Update cart UI elements (count badge, drawer)
  updateCartUI() {
    const cartCount = this.cart ? this.cart.item_count : 0;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cartCount;
      el.style.display = cartCount > 0 ? 'flex' : 'none';
    });

    this.updateCartDrawer();
  }

  // Update cart drawer/modal content
  updateCartDrawer() {
    const cartDrawer = document.querySelector('#shoppingCart');
    if (!cartDrawer || !this.cart) return;

    const itemsContainer = cartDrawer.querySelector('.tf-mini-cart-items');
    if (!itemsContainer) return;

    itemsContainer.innerHTML = '';

    if (this.cart.items.length === 0) {
      itemsContainer.innerHTML = `
        <div class="tf-mini-cart-empty">
          <p style="margin-left: 40px; margin-top: 10px;">Your cart is empty</p>
          <a href="/collections/all" class="tf-btn btn-fill">Continue Shopping</a>
        </div>
      `;
      cartDrawer.querySelector('.tf-mini-cart-bottom')?.classList.add('d-none');
      return;
    }

    cartDrawer.querySelector('.tf-mini-cart-bottom')?.classList.remove('d-none');

    this.cart.items.forEach((item, index) => {
      const itemHTML = this.createCartItemHTML(item, index + 1);
      itemsContainer.insertAdjacentHTML('beforeend', itemHTML);
    });

    this.updateCartTotals();
  }

  // Create HTML for a cart item in drawer
  createCartItemHTML(item, line) {
    const itemPrice = this.formatMoney(item.final_price);

    return `
      <div class="tf-mini-cart-item" data-line="${line}">
        <div class="tf-mini-cart-image">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.product_title}">
          </a>
        </div>
        <div class="tf-mini-cart-info">
          <a class="title link" href="${item.url}">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="meta-variant">${item.variant_title}</div>` : ''}
          <div class="price fw-6">${itemPrice}</div>
          <div class="tf-mini-cart-btns">
            <div class="wg-quantity small">
              <span class="btn-quantity minus-btn drawer-qty-btn" data-line="${line}" data-action="decrease">-</span>
              <input type="text" name="number" value="${item.quantity}" readonly>
              <span class="btn-quantity plus-btn drawer-qty-btn" data-line="${line}" data-action="increase">+</span>
            </div>
            <div class="tf-mini-cart-remove drawer-remove-btn" data-line="${line}">Remove</div>
          </div>
        </div>
      </div>
    `;
  }

  // Update cart totals in drawer
  updateCartTotals() {
    const cartDrawer = document.querySelector('#shoppingCart');
    if (!cartDrawer || !this.cart) return;

    const subtotalEl = cartDrawer.querySelector('.tf-totals-total-value');
    if (subtotalEl) {
      subtotalEl.textContent = this.formatMoney(this.cart.total_price);
    }

    this.updateShippingProgress();
  }

  // Update free shipping progress bar
  updateShippingProgress() {
    const progressBar = document.querySelector('.tf-progress-bar span');
    const progressMsg = document.querySelector('.tf-progress-msg');

    if (!progressBar || !progressMsg || !this.cart) return;

    const freeShippingThreshold = 7500;
    const currentTotal = this.cart.total_price;
    const remaining = freeShippingThreshold - currentTotal;
    const percentage = Math.min((currentTotal / freeShippingThreshold) * 100, 100);

    progressBar.style.width = `${percentage}%`;

    if (remaining > 0) {
      progressMsg.innerHTML = `Buy <span class="price fw-6">${this.formatMoney(remaining)}</span> more to enjoy <span class="fw-6">Free Shipping</span>`;
    } else {
      progressMsg.innerHTML = `<span class="fw-6">You've qualified for Free Shipping!</span>`;
    }
  }

  // Show cart drawer/modal
  showCartDrawer() {
    const cartModal = document.querySelector('#shoppingCart');
    if (cartModal && typeof bootstrap !== 'undefined') {
      const bsModal = new bootstrap.Modal(cartModal);
      bsModal.show();
    }
  }

  // Setup ALL event listeners (single source of truth)
  setupEventListeners() {
    // Use event delegation on document for ALL cart interactions
    document.addEventListener('click', async (e) => {
      const target = e.target;

      // Cart page: Plus/Minus buttons (class: btn-quantity, data-type: plus/minus)
      if (target.classList.contains('btn-quantity') && target.dataset.type) {
        e.preventDefault();
        e.stopPropagation();
        if (this.updating) return;

        const wrapper = target.closest('.wg-quantity');
        const input = wrapper?.querySelector('.quantity-input');
        if (!input) return;

        const currentQty = parseInt(input.value) || 1;
        const line = parseInt(input.dataset.line);
        const isPlus = target.dataset.type === 'plus';

        const newQty = isPlus ? currentQty + 1 : Math.max(0, currentQty - 1);
        input.value = newQty;

        await this.updateItem(line, newQty);
        return;
      }

      // Drawer: Plus/Minus buttons (class: drawer-qty-btn, data-action: increase/decrease)
      if (target.classList.contains('drawer-qty-btn')) {
        e.preventDefault();
        e.stopPropagation();
        if (this.updating) return;

        const line = parseInt(target.dataset.line);
        const action = target.dataset.action;
        const item = target.closest('.tf-mini-cart-item');
        const input = item?.querySelector('input[name="number"]');
        if (!input) return;

        let qty = parseInt(input.value) || 1;
        if (action === 'increase') {
          qty++;
        } else if (action === 'decrease' && qty > 1) {
          qty--;
        }

        input.value = qty;
        await this.updateItem(line, qty);
        return;
      }

      // Cart page: Remove button
      const removeBtn = target.closest('.remove-cart');
      if (removeBtn && removeBtn.dataset.line) {
        e.preventDefault();
        e.stopPropagation();
        if (this.updating) return;

        await this.updateItem(parseInt(removeBtn.dataset.line), 0);
        return;
      }

      // Drawer: Remove button
      if (target.classList.contains('drawer-remove-btn')) {
        e.preventDefault();
        e.stopPropagation();
        if (this.updating) return;

        const item = target.closest('.tf-mini-cart-item');
        if (item) {
          item.style.opacity = '0.5';
          item.style.pointerEvents = 'none';
        }

        await this.updateItem(parseInt(target.dataset.line), 0);
        return;
      }
    });

    // Cart page: Handle direct input changes
    document.addEventListener('change', async (e) => {
      if (!e.target.classList.contains('quantity-input')) return;
      if (this.updating) return;

      const qty = Math.max(0, parseInt(e.target.value) || 0);
      const line = parseInt(e.target.dataset.line);
      await this.updateItem(line, qty);
    });

    // Handle add to cart forms
    document.addEventListener('submit', async (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        e.preventDefault();

        const form = e.target;
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn?.innerHTML;

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Adding...';
        }

        try {
          const formData = new FormData(form);
          const data = {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity') || 1),
            properties: {}
          };

          for (let [key, value] of formData.entries()) {
            if (key.startsWith('properties[')) {
              const propName = key.match(/properties\[(.*?)\]/)[1];
              data.properties[propName] = value;
            }
          }

          await this.addItem(data);

          if (submitBtn) {
            submitBtn.innerHTML = '<span class="icon-check me-2"></span>Added!';
            setTimeout(() => {
              submitBtn.disabled = false;
              submitBtn.innerHTML = originalText;
            }, 2000);
          }
        } catch (error) {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
          }
        }
      }
    });

    // Listen for cart refresh requests
    document.addEventListener('cart:refresh', async () => {
      await this.fetchCart();
      this.updateCartUI();
    });
  }

  // Show error message
  showError(message) {
    const toast = document.createElement('div');
    toast.className = 'cart-error-toast';
    toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#dc3545;color:#fff;padding:15px 20px;border-radius:8px;z-index:9999;';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  // Format money
  formatMoney(cents, format = '${{amount}}') {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    };

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return cents;

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartManager = new CartManager();
  });
} else {
  window.cartManager = new CartManager();
}

window.CartManager = CartManager;
